import { Check } from '@mui/icons-material';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { LegalEntity } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormSwitch from '../../components/forms/FormSwitch';
import FormTextField from '../../components/forms/FormTextField';
import PrimaryButton from '../../components/PrimaryButton';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { useSnackbar } from '../../contexts/snackbar.context';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';

type Props = {
  open: boolean;
  legalEntity?: DbDocument<LegalEntity> | null;
  onClose: () => void;
};

const EditLegalEntity = ({ legalEntity, open, onClose }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();
  const { addSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');

  const form = useForm<LegalEntity>({
    mode: 'onChange',
    defaultValues: {
      name: legalEntity?.name || '',
      prroCheckBox: legalEntity?.prroCheckBox || {
        enabled: false,
        automatic: false,
        cashierName: '',
        cashierToken: '',
        key: '',
      },
    },
  });

  const { watch, setValue } = form;
  const prroCheckBox = watch('prroCheckBox');

  const handleClose = () => {
    onClose();
  };

  const saveChanges = (newLegalEntity: LegalEntity) => {
    if (legalEntity?.id) {
      updateDoc<LegalEntity>(`cafes/${cafeSlug}/legalEntities/${legalEntity.id}`, newLegalEntity);
    } else {
      const id = uniqid();
      setDoc<LegalEntity>(`cafes/${cafeSlug}/legalEntities/${id}`, newLegalEntity);
    }
    onClose();
  };

  const removeProduct = async () => {
    if (legalEntity?.id) {
      deleteDoc(`cafes/${cafeSlug}/legalEntities/${legalEntity.id}`);
      onClose();
    }
  };

  const loginCashier = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('https://api.checkbox.in.ua/api/v1/cashier/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Name': 'FireMenu',
          'X-Client-Version': 'v0.1.1',
        },
        body: JSON.stringify({ login, password }),
      });

      const resJson = await res.json();

      if (res.status !== 200) {
        addSnackbar(resJson.message || 'Упс, щось пішло не так', 'error');
        return;
      }

      const res2 = await fetch('https://api.checkbox.in.ua/api/v1/cashier/me', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${resJson.access_token}`,
          'X-Client-Name': 'FireMenu',
          'X-Client-Version': 'v0.1.1',
        },
      });

      const res2Json = await res2.json();

      setValue('prroCheckBox.cashierToken', resJson.access_token || '', { shouldDirty: true });
      setValue('prroCheckBox.cashierName', res2Json.full_name || '', { shouldDirty: true });
    } catch (e) {
      addSnackbar('Упс, щось пішло не так', 'error');
    } finally {
      setLoading(false);
    }
  }, [addSnackbar, login, password, setValue]);

  const logoutCashier = useCallback(() => {
    setValue('prroCheckBox.cashierToken', '', { shouldDirty: true });
    setValue('prroCheckBox.cashierName', '', { shouldDirty: true });
  }, [setValue]);

  return (
    <EditModal<LegalEntity>
      title={t.legal_entity[lang]}
      open={open}
      onClose={handleClose}
      submitButton={legalEntity ? t.save_changes[lang] : t.add[lang]}
      submitButtonProps={{
        disabled: prroCheckBox.enabled && (!prroCheckBox.key || !prroCheckBox.cashierToken),
      }}
      onSubmit={saveChanges}
      secondaryButton={legalEntity ? t.remove[lang] : undefined}
      confirmText={t.remove_legal_entity_confirm[lang]}
      onSecondaryButtonClick={removeProduct}
      form={form}
    >
      <Box sx={{ py: 2 }}>
        <FormTextField name="name" label={t.name[lang]} rules={{ required: true }} fullWidth sx={{ mb: 3 }} />

        {cafe?.country === 'ua' && <FormSwitch label="Активувати ПРРО" name="prroCheckBox.enabled" />}

        {prroCheckBox.enabled && (
          <Box sx={{ p: 2, bgcolor: (t) => `${t.palette.secondary.light}22`, borderRadius: 1 }}>
            <Typography sx={{ color: (t) => t.palette.secondary.main, textAlign: 'center' }}>
              Налаштування ПРРО
            </Typography>

            <Alert severity="info" sx={{ background: 'none' }}>
              FireMenu забезпечує підтримку ПРРО завдяки інтеграції з сервісом{' '}
              <Link href="https://my.checkbox.ua/auth/registration?partner_ref=gHidEQnmlt" target="_blank">
                Checkbox
              </Link>
              . Для отримання більш детальної інформації та для налаштування цієї функції, будь ласка, зверніться до
              нашої підтримки за телефоном <Link href="tel:+380976829856">+38 097 682 98 56</Link>.
              <Link href="https://my.checkbox.ua/auth/registration?partner_ref=gHidEQnmlt" target="_blank">
                <Button color="secondary">Зареєструватись у Checkbox</Button>
              </Link>
            </Alert>

            <FormSwitch label="Автоматично фіскалізувати чеки при їх оплаті" name="prroCheckBox.automatic" />

            <Typography sx={{ mb: 1, mt: 1 }}>Каса</Typography>
            <FormTextField sx={{ mb: 3 }} label="Ключ ліцензії каси" name="prroCheckBox.key" fullWidth size="small" />

            <Typography sx={{ mb: 1 }}>Касир</Typography>

            {prroCheckBox.cashierName && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 1 }}>
                {prroCheckBox.cashierToken && <Check fontSize="small" color="success" sx={{ mr: 1 }} />}
                <Typography>{prroCheckBox.cashierName}</Typography>
              </Box>
            )}

            {prroCheckBox.cashierName && <PrimaryButton onClick={logoutCashier}>Змінити касира</PrimaryButton>}

            {!prroCheckBox.cashierToken && (
              <Box sx={{ maxWidth: 300 }}>
                <StyledTextField
                  label="Логін"
                  value={login}
                  fullWidth
                  size="small"
                  disabled={loading}
                  onChange={(e) => setLogin(e.target.value)}
                  color="secondary"
                  sx={{ mb: 2 }}
                />

                <StyledTextField
                  label="Пароль"
                  value={password}
                  fullWidth
                  size="small"
                  type="password"
                  disabled={loading}
                  onChange={(e) => setPassword(e.target.value)}
                  color="secondary"
                  sx={{ mb: 2 }}
                />

                <PrimaryButton
                  fullWidth
                  sx={{ mb: 2 }}
                  color="secondary"
                  disabled={loading || !login || !password}
                  variant="contained"
                  onClick={loginCashier}
                >
                  Авторизувати касира
                </PrimaryButton>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </EditModal>
  );
};

export default EditLegalEntity;
